import React, { useState } from 'react';
import {
    Button, FormLabel, Grid, IconButton,
} from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { getJsonPath } from '../../utils/constants';
import { LoginFormValues } from '../../types/LogInTypes';
import { loginFormFieldLabel, loginFormFieldName } from './loginContainerUtils';
import { FormikTextField } from '../../components/Formik';

const alignLeft = {
    textAlign: 'left',
};
const illustrationContainer = {
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    height: '100vh',
    justifyContent: 'center',
    background: '#D6E6F0',
};

const logoStyle: object = {
    position: 'fixed',
    top: '64px',
    left: '64px',
};

const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    height: '100vh',
    color: '#666666',
    justifyContent: 'center',
};

const pageStyle = {
    display: 'flex',
    alignItems: 'center',
    alignContent: 'center',
    height: '100vh',
    justifyContent: 'center',
};

const style: object = {
    minHeight: '400px',
    padding: '8px 8px 8px 8px',
    textAlign: 'center',
};

const formStyle = {
    margin: '16px 8px 8px 8px',
    padding: '20px 40px 0px 40px',
};

const titleStyle = {
    color: '#1C1C1C',
    fontWeight: 500,
    fontSize: 24,
    margin: '0.35em 0',
};

const subtitleStyle = {
    color: 'grey',
    margin: 0,
};

const labelStyle: object = {
    color: '#666666',
    textAlign: 'left',
    float: 'left',
};

interface Props {
    logInErrorMessage: string,
    showTwoFactorAuthCode: boolean
}

const LoginForm = (props: Props) => {
    const [showPassword, setShowPassword] = useState(false);
    const { handleSubmit } = useFormikContext<LoginFormValues>();
    const {
        logInErrorMessage,
        showTwoFactorAuthCode,
    } = props;

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            handleSubmit();
        }
    };
    const onLoginClick = () => {
        handleSubmit();
    };

    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };

    return (

        <Grid container>
            <Grid item sm={12} style={logoStyle}>
                <img
                    src={getJsonPath('/LoginScreen/nuacare_logo.svg')}
                    alt="nuacare-logo"
                    width="250"
                />
            </Grid>
            <Grid item sm={6}>
                <div style={illustrationContainer}>
                    <img
                        src={getJsonPath('/LoginScreen/img_hero2x.png')}
                        alt="nuacare-logo-2"
                        width="50%"
                    />
                </div>
            </Grid>
            <Grid item sm={6} style={pageStyle}>
                <div style={containerStyle}>
                    <div style={style}>
                        <div style={formStyle}>
                            <Grid container style={{ textAlign: 'left' }}>
                                <Grid item sm={12} md={12} lg={12} style={{ textAlign: 'center' }}>
                                    <h3 style={titleStyle}>Welcome to Nuacare CDS</h3>
                                    <p style={subtitleStyle}>
                                        Enter your username & password to login
                                    </p>
                                </Grid>
                            </Grid>
                            <Grid container style={{ margin: '4em 1em 4em 1em' }}>
                                <Grid container item justifyContent="center">
                                    <>
                                        <Grid item md={7} sm={12} style={{ margin: '0.4em 0 0.4em 0' }}>
                                            <FormLabel style={labelStyle}>
                                                {loginFormFieldLabel.USER_NAME}
                                            </FormLabel>
                                        </Grid>
                                        <Grid item md={7} sm={12}>
                                            <Field
                                                component={FormikTextField}
                                                name={loginFormFieldName.USER_NAME}
                                                onKeyDown={handleKeyDown}
                                                label=""
                                                fullWidth
                                                InputProps={{ inputProps: { min: 0 } }}
                                                autoFocus
                                                test-id="login-username"
                                            />
                                        </Grid>
                                        <Grid item md={7} sm={12} style={{ margin: '0.4em 0 0.4em 0' }}>
                                            <FormLabel style={labelStyle}>
                                                {loginFormFieldLabel.PASSWORD}
                                            </FormLabel>
                                        </Grid>
                                        <Grid item md={7} sm={12} style={{ position: 'relative' }}>
                                            <Field
                                                component={FormikTextField}
                                                name={loginFormFieldName.PASSWORD}
                                                label=""
                                                fullWidth
                                                InputProps={{ inputProps: { min: 0 } }}
                                                onKeyDown={handleKeyDown}
                                                type={showPassword ? 'text' : 'password'}
                                                test-id="login-password"
                                            />
                                            <IconButton
                                                onClick={togglePasswordVisibility}
                                                style={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    right: 0,
                                                    transform: 'translateY(-50%)',
                                                }}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </Grid>
                                        {
                                            showTwoFactorAuthCode && (
                                                <>
                                                    <Grid item md={7} sm={12} style={{ margin: '0.4em 0 0.4em 0' }}>
                                                        <FormLabel style={labelStyle}>
                                                            {loginFormFieldLabel
                                                                .TWO_FACTOR_AUTH_CODE}
                                                        </FormLabel>
                                                    </Grid>
                                                    <Grid item md={7} sm={12}>
                                                        <Field
                                                            component={FormikTextField}
                                                            name={loginFormFieldName
                                                                .TWO_FACTOR_AUTH_CODE}
                                                            onKeyDown={handleKeyDown}
                                                            label=""
                                                            fullWidth
                                                            InputProps={{ inputProps: { min: 0 } }}
                                                            autoFocus
                                                            test-id="login-twoFactorAuthCode"
                                                        />
                                                    </Grid>
                                                </>
                                            )
                                        }
                                        <Grid
                                            item
                                            sm={7}
                                            container
                                            marginTop={2}
                                            sx={{
                                                backgroundColor: logInErrorMessage ? '#F9EAEC' : '',
                                                padding: '0.5em 1.5em 0.5em 0.6em',
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Grid
                                                item
                                                sm={12}
                                                md={12}
                                                lg={12}
                                                style={{
                                                    textAlign: 'left',
                                                }}
                                            >
                                                <span style={{ color: 'red', marginLeft: 2, fontSize: 14 }}>
                                                    {' '}
                                                    {logInErrorMessage}
                                                    {' '}
                                                </span>
                                            </Grid>
                                        </Grid>
                                        <Grid
                                            container
                                            item
                                            sm={7}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                paddingBottom: '2.5em',
                                                paddingTop: '2.5em',
                                            }}
                                        >
                                            <Grid
                                                item
                                                lg={6}
                                                md={6}
                                                sm={6}
                                                sx={{ ...alignLeft, width: '100%' }}
                                            />
                                            <Grid
                                                item
                                                sm={12}
                                                style={{ textAlign: 'right', width: '100%' }}
                                            >
                                                <Button
                                                    variant="contained"
                                                    fullWidth
                                                    onClick={onLoginClick}
                                                    test-id="login-page-button"
                                                >
                                                    Login
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </>
                                </Grid>
                            </Grid>
                        </div>
                    </div>
                </div>
            </Grid>
        </Grid>
    );
};

export default LoginForm;
